import { useMemo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useAdsWorkspaceStore } from "../store/useAdsWorkspaceStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import {
  useAdsResearchStore,
  type ExistingUgcElementSummary,
  type UgcElementKind,
  type UgcPlannedElement,
  type UgcVideoElementPlan,
} from "./useAdsResearchStore";
import { useElementPlansForProduct } from "../data/useElementPlansForProduct";

const KIND_LABEL: Record<UgcElementKind, string> = {
  "avatar-anchor": "avatar anchor",
  "avatar-pose": "avatar pose",
  product: "product",
  item: "item",
  background: "background",
  "b-roll": "b-roll",
};

function isCached(
  element: UgcPlannedElement,
  existing: Record<string, ExistingUgcElementSummary>,
): boolean {
  if (element.kind === "avatar-anchor") return true;
  const match = existing[element.elementKey];
  return !!match && typeof match.klingElementId === "number";
}

function countNewElements(
  plan: UgcVideoElementPlan,
  existing: Record<string, ExistingUgcElementSummary>,
): number {
  return plan.elements.reduce((n, el) => (isCached(el, existing) ? n : n + 1), 0);
}

const REGENERATE_PROMPT =
  "Regenerate the UGC element plans. Call ads_plan_ugc_elements again using the scripts (ugcBrief.videoScripts) and personas (ugcBrief.avatars) from the current UGC Research Brief, the current workspace, and the selected product. Then present the refreshed plan.";

export const UGC_ELEMENT_PLAN_CONFIRM_PROMPT =
  "The element plans look good. Proceed — call ads_generate_ugc_element_refs using the current element plans and the most recent avatar portraits from Generated Assets (matched to each script via assignedAvatarId). Use the current workspace and product. Tell me once the Banan sessions are dispatched.";

/** Non-modal review panel that lists every video's element plan, text frames,
 *  and beats. Used inline in the moodboard step (Research → Step 4) and also
 *  wrapped by `ElementPlanReviewModal` for the Studio tab. */
export function ElementPlanReviewPanel({
  onAfterConfirm,
  emptyState,
}: {
  onAfterConfirm?: () => void;
  /** Optional content rendered when no plan exists. Lets parents (e.g. the
   *  inline moodboard step) provide a dispatch CTA. */
  emptyState?: ReactNode;
}) {
  const existingElementsByKey = useAdsResearchStore((s) => s.existingElementsByKey);
  const adCopy = useAdsResearchStore((s) => s.adCopy);
  const generatedAds = useAdsWorkspaceStore((s) => s.generatedAds);
  const selectedProductIds = useAdsWorkspaceStore((s) => s.selectedProductIds);
  const products = useAdsWorkspaceStore((s) => s.products);
  const workspaceId = useAdsWorkspaceStore((s) => s.scopeWorkspaceId);
  const requestCompanionPrompt = useAdsWorkspaceStore((s) => s.requestCompanionPrompt);

  const selectedProduct = useMemo(() => {
    const id = selectedProductIds[0] ?? null;
    if (!id) return null;
    const p = products.find((x) => x.id === id);
    return p ? { id: p.id, title: p.title } : null;
  }, [products, selectedProductIds]);

  const plansByVideoIndex = useElementPlansForProduct(workspaceId, selectedProduct);

  // Build (scriptAngle, plan) entries ordered by videoIndex so the modal
  // renders Video 1, 2, … in the same order Marcus produced them.
  const entries = useMemo<Array<[string, UgcVideoElementPlan]>>(() => {
    const out: Array<[string, UgcVideoElementPlan]> = [];
    const indices = Object.keys(plansByVideoIndex)
      .map((k) => Number(k))
      .sort((a, b) => a - b);
    for (const idx of indices) {
      const record = plansByVideoIndex[idx];
      if (!record) continue;
      const angle = record.script?.angle ?? `video-${idx}`;
      out.push([angle, record.elementPlan]);
    }
    return out;
  }, [plansByVideoIndex]);

  const totals = useMemo(() => {
    let newEls = 0;
    let cachedEls = 0;
    let textFrames = 0;
    for (const [, plan] of entries) {
      for (const el of plan.elements) {
        if (isCached(el, existingElementsByKey)) cachedEls++;
        else newEls++;
      }
      textFrames += plan.textFrames?.length ?? 0;
    }
    return { newEls, cachedEls, textFrames };
  }, [entries, existingElementsByKey]);

  const handleRegenerate = () => requestCompanionPrompt(REGENERATE_PROMPT);

  const handleConfirm = () => {
    requestCompanionPrompt(UGC_ELEMENT_PLAN_CONFIRM_PROMPT);
    onAfterConfirm?.();
  };

  if (entries.length === 0) {
    return (
      <>{emptyState ?? (
        <p className="text-sm text-muted-foreground">No plan yet — generate one first.</p>
      )}</>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 pb-4 border-b border-border/50">
        <div className="space-y-1 min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Element plan
          </p>
          <p className="text-sm text-foreground">
            <Stat n={entries.length} /> video{entries.length === 1 ? "" : "s"}
            <Sep />
            <Stat n={totals.newEls} /> new
            <Sep />
            <Stat n={totals.cachedEls} /> cached
            <Sep />
            <Stat n={totals.textFrames} /> text frame{totals.textFrames === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            className="rounded-full"
          >
            Regenerate
          </Button>
          <Button size="sm" onClick={handleConfirm} className="rounded-full">
            Generate refs
          </Button>
        </div>
      </div>

      <div className="space-y-10">
        {entries.map(([scriptAngle, plan], idx) => {
          const script = adCopy?.videoScripts.find((s) => s.angle === scriptAngle);
          const avatarId = script?.assignedAvatarId;
          let avatarImageUrl: string | undefined;
          if (avatarId) {
            const avatarAd = generatedAds.find(
              (ad) =>
                ad.type === "image" &&
                ad.metadata?.sourceAvatarPersonaId === avatarId &&
                ad.result.status === "done" &&
                ad.result.output,
            );
            if (avatarAd && avatarAd.type === "image") {
              avatarImageUrl = avatarAd.result.output;
            }
          }
          return (
            <VideoPlanSection
              key={scriptAngle}
              videoIndex={idx}
              scriptAngle={scriptAngle}
              plan={plan}
              existingElementsByKey={existingElementsByKey}
              avatarImageUrl={avatarImageUrl}
            />
          );
        })}
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ElementPlanReviewModal({ open, onClose }: Props) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl flex flex-col p-0 gap-0 overflow-hidden bg-card border-border/40 max-h-[90vh]">
        <DialogHeader className="px-8 pt-6 pb-4 border-b border-border/40 shrink-0">
          <DialogTitle className="text-base font-semibold tracking-tight text-foreground">
            Review UGC element plan
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            What Marcus has planned per script before Banan generates references.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <ElementPlanReviewPanel onAfterConfirm={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Per-video section ───────────────────────────────────────────────────────

function VideoPlanSection({
  videoIndex,
  scriptAngle,
  plan,
  existingElementsByKey,
  avatarImageUrl,
}: {
  videoIndex: number;
  scriptAngle: string;
  plan: UgcVideoElementPlan;
  existingElementsByKey: Record<string, ExistingUgcElementSummary>;
  avatarImageUrl?: string;
}) {
  const newCount = countNewElements(plan, existingElementsByKey);
  const cachedCount = plan.elements.length - newCount;

  return (
    <section>
      <header className="flex items-start gap-3 pb-3 border-b border-border/40">
        {avatarImageUrl && (
          <img
            src={avatarImageUrl}
            alt=""
            className="w-10 h-10 rounded-full object-cover border border-border/50 shrink-0 mt-0.5"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Video {videoIndex + 1}
          </p>
          <p className="text-sm font-medium text-foreground leading-snug">{scriptAngle}</p>
          <p className="text-[11px] text-muted-foreground/80 mt-1">
            <Stat n={plan.elements.length} /> element{plan.elements.length === 1 ? "" : "s"}
            <Sep />
            <Stat n={newCount} /> new
            <Sep />
            <Stat n={cachedCount} /> cached
            <Sep />
            <Stat n={plan.textFrames?.length ?? 0} /> text frame{(plan.textFrames?.length ?? 0) === 1 ? "" : "s"}
            <Sep />
            <Stat n={plan.beats.length} /> beat{plan.beats.length === 1 ? "" : "s"}
          </p>
        </div>
      </header>

      <div className="mt-5 space-y-6">
        <SubSection title="Elements" count={plan.elements.length}>
          {plan.elements.map((el, i) => (
            <ElementRow
              key={`${el.elementKey}-${i}`}
              index={i}
              element={el}
              cached={isCached(el, existingElementsByKey)}
              existing={existingElementsByKey[el.elementKey]}
            />
          ))}
        </SubSection>

        {(plan.textFrames?.length ?? 0) > 0 && (
          <SubSection title="Text frames" count={plan.textFrames?.length ?? 0}>
            {(plan.textFrames ?? []).map((tf, i) => (
              <Row key={i} index={i}>
                <p className="text-sm leading-snug">
                  <Tag>{tf.assignedBeat}</Tag>
                  <span className="font-medium text-foreground">"{tf.overlayText}"</span>
                  {tf.includesAvatar && (
                    <span className="ml-2 text-[11px] text-muted-foreground">+ avatar</span>
                  )}
                </p>
                <p className="text-[12px] text-muted-foreground/90 leading-relaxed whitespace-pre-wrap break-words mt-1">
                  {tf.prompt}
                </p>
              </Row>
            ))}
          </SubSection>
        )}

        <SubSection title="Beats" count={plan.beats.length}>
          {plan.beats.map((beat, i) => (
            <Row key={`${beat.beatType}-${i}`} index={i}>
              <Tag>{beat.beatType}</Tag>
              <BeatPromptWithPlaceholders prompt={beat.prompt} />
            </Row>
          ))}
        </SubSection>
      </div>
    </section>
  );
}

// ── Building blocks ─────────────────────────────────────────────────────────

function SubSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 pb-2 border-b border-border/30">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {title}
        </h4>
        <span className="text-[10px] font-mono text-muted-foreground/60">{count}</span>
      </div>
      <ol className="divide-y divide-border/30">{children}</ol>
    </div>
  );
}

function Row({ index, children }: { index: number; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[2.25rem_minmax(0,1fr)] gap-3 py-3 items-start">
      <span className="text-[11px] font-mono text-muted-foreground/60 pt-[3px]">
        {(index + 1).toString().padStart(2, "0")}
      </span>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

function ElementRow({
  index,
  element,
  cached,
  existing,
}: {
  index: number;
  element: UgcPlannedElement;
  cached: boolean;
  existing: ExistingUgcElementSummary | undefined;
}) {
  return (
    <li className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] gap-3 py-3 items-start">
      <span className="text-[11px] font-mono text-muted-foreground/60 pt-[3px]">
        {(index + 1).toString().padStart(2, "0")}
      </span>
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium text-foreground leading-snug">{element.elementName}</p>
        <p className="text-[11px] text-muted-foreground/80">
          {KIND_LABEL[element.kind]}
          <Sep />
          <code className="font-mono">{element.elementKey}</code>
        </p>
        {element.elementDescription && (
          <p className="text-[12px] text-muted-foreground/90 leading-relaxed">
            {element.elementDescription}
          </p>
        )}
        {element.prompt && (
          <p className="text-[12px] text-muted-foreground/70 leading-relaxed whitespace-pre-wrap break-words">
            {element.prompt}
          </p>
        )}
      </div>
      <span
        className={cn(
          "shrink-0 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] pt-[3px]",
          cached ? "text-muted-foreground/60" : "text-foreground",
        )}
        title={
          cached
            ? existing?.klingElementId
              ? `Reusing registered element #${existing.klingElementId}`
              : "Provided by dispatch layer (avatar anchor)"
            : "Banan will generate this"
        }
      >
        {!cached && <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden />}
        {cached ? "cached" : "new"}
      </span>
    </li>
  );
}

function BeatPromptWithPlaceholders({ prompt }: { prompt: string }) {
  const parts = useMemo(() => splitByPlaceholder(prompt), [prompt]);
  return (
    <p className="text-[12px] text-muted-foreground/90 leading-relaxed break-words mt-1">
      {parts.map((part, i) => {
        if (part.kind === "text") return <span key={i}>{part.value}</span>;
        return (
          <code
            key={i}
            className="font-mono rounded px-1 py-0.5 mx-0.5 bg-muted text-foreground/80"
          >
            {part.kind === "element" ? "element_" : "image_"}
            {part.index}
          </code>
        );
      })}
    </p>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground mr-2">
      {children}
    </span>
  );
}

function Stat({ n }: { n: number }) {
  return <span className="font-mono text-foreground">{n}</span>;
}

function Sep() {
  return <span className="mx-1.5 text-muted-foreground/40">·</span>;
}

type PromptPart =
  | { kind: "text"; value: string }
  | { kind: "element" | "image"; index: number };

function splitByPlaceholder(prompt: string): PromptPart[] {
  const out: PromptPart[] = [];
  const re = /<<<(element|image)_(\d+)>>>/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(prompt)) !== null) {
    if (match.index > last) {
      out.push({ kind: "text", value: prompt.slice(last, match.index) });
    }
    out.push({
      kind: match[1] as "element" | "image",
      index: Number(match[2]),
    });
    last = match.index + match[0].length;
  }
  if (last < prompt.length) {
    out.push({ kind: "text", value: prompt.slice(last) });
  }
  return out;
}
